<template>
  <div
    :class="[
      'billing-footer w-full py-8 px-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4 relative',
      enableButton || isCustom ? 'gradient_background' : 'bg-gray-200 border-t border-gray-300',
    ]"
  >
    <div class="flex flex-row gap-2 justify-between w-full px-28">
      <div class="flex flex-col justify-center relative">
        <!-- Placeholder for left side content if needed -->
      </div>
      <div class="flex flex-row items-center mt-4 md:mt-0 ml-auto">
        <div
          v-for="item in summaryItems"
          :key="item.label"
          class="flex flex-col items-center md:items-start mr-8 last:mr-0"
          :class="enableButton || isCustom ? 'text-white' : 'text-gray-900'"
          :style="isCustom ? 'visibility: hidden;' : ''"
        >
          <span class="text-[13px]">{{ item.label }}</span>
          <div class="flex flex-row items-center">
            <div v-if="item.displayValue === 'loading'" class="flex items-center h-[36px]">
              <div class="animate-spin rounded-full h-5 w-5 border-b-2 items-center" :class="enableButton || isCustom ? 'border-white' : 'border-gray-700'"></div>
            </div>
            <template v-else>
              <span class="text-[24px] font-bold">{{ item.displayValue }}</span>
              <span class="text-[13px] ml-1">/{{ getBillingPeriodDisplayText(billingPeriod) }}</span>
            </template>
          </div>
        </div>
        <a-button :class="buttonClass" :disabled="shouldDisableButton" :loading="isProcessing || props.loading || props.isProcessingCheckout || isCalculatingDue" @click="handleButtonClick">
          {{ buttonText }}
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { getBilledV3Label, getBillingPeriodDisplayText } from '@/utils/billingPeriodUtils';

const props = defineProps({
  billedMonthly: {
    type: [String, Number],
    required: true,
  },
  dueToday: {
    type: [String, Number],
    required: true,
  },
  creditPacks: {
    type: Number,
    default: 0,
  },
  billingPeriod: {
    type: String,
    default: 'month',
  },
  creditPacksAmount: {
    type: Number,
    default: 0,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  enableButton: {
    type: Boolean,
    default: true,
  },
  selectedCreditPack: Object,
  isProcessingCheckout: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  trialDays: {
    type: Number,
    default: 0,
  },
  isCalculatingDue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['setup-billing', 'cancel-trial']);
const isProcessing = ref(false);

const isCustom = computed(() => {
  const isCustomCreditPack = props.selectedCreditPack && props.selectedCreditPack.isCustom;
  const isFreeTrialCancelling = props.status === 'FREE_TRIAL' && props.creditPacks === 0;
  return isCustomCreditPack || isFreeTrialCancelling;
});

const shouldDisableButton = computed(() => {
  // Talk to Sales is always enabled
  if (isCustom.value) return false;
  // Otherwise use normal logic - disable during loading, checkout processing, or when button is disabled
  return !props.enableButton || props.loading || props.isProcessingCheckout;
});

const summaryItems = computed(() => [
  {
    label: getBilledV3Label(props.billingPeriod),
    value: props.loading ? 'loading' : props.billedMonthly,
    displayValue: props.loading ? 'loading' : '$' + props.billedMonthly,
  },
  {
    label: 'Due Today',
    value: props.loading ? 'loading' : props.dueToday,
    displayValue: props.loading ? 'loading' : '$' + props.dueToday,
  },
]);

const handleButtonClick = async () => {
  if (isProcessing.value) return;

  isProcessing.value = true;

  try {
    if (props.selectedCreditPack && props.selectedCreditPack.isCustom) {
      window.open('https://meetings.superagi.com/Sales-Demo-Team', '_blank');
      return;
    }

    // For FREE_TRIAL users cancelling, emit cancel trial event
    if (props.status === 'FREE_TRIAL' && props.creditPacks === 0) {
      emit('cancel-trial');
      return;
    }

    emit('setup-billing');
  } finally {
    setTimeout(() => {
      isProcessing.value = false;
    }, 500);
  }
};

const buttonText = computed(() => {
  if (props.isCalculatingDue) {
    return 'Calculating...';
  }

  if (props.selectedCreditPack && props.selectedCreditPack.isCustom) {
    return 'Talk to Sales';
  }

  // For FREE_TRIAL users cancelling
  if (props.status === 'FREE_TRIAL' && props.creditPacks === 0) {
    return 'Cancel Trial';
  }

  return 'Update Billing';
});

const buttonClass = computed(() => {
  // If enabled (including Talk to Sales), use gradient style
  if (props.enableButton || isCustom.value) {
    return 'primary_medium_dark font-semibold px-8';
  }
  return 'primary_medium font-semibold';
});
</script>

<style scoped>
.billing-footer {
  height: 90px;
}
.billing_button {
  transition: box-shadow 0.2s;
}
.billing_button:hover {
  box-shadow: 0 4px 16px rgba(112, 149, 235, 0.18);
}

.text-color-billing {
  color: rgba(255, 255, 255, 0.6);
}
</style>
